import * as React from 'react'
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'

import { cn } from '@/lib/utils'

/**
 * Menu thả xuống dùng cho nút kebab (…) gom hành động phụ trên mobile.
 *
 * Radix unmount `Content` khi menu đóng, nên KHÔNG đặt `Dialog` bên trong
 * `DropdownMenuContent` — dialog sẽ bị gỡ ngay khi menu đóng và không bao giờ
 * mở được. Đặt dialog ở component cha, menu item chỉ set state mở dialog.
 */
function DropdownMenu({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
    return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuTrigger({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
    return (
        <DropdownMenuPrimitive.Trigger
            data-slot="dropdown-menu-trigger"
            {...props}
        />
    )
}

function DropdownMenuContent({
    className,
    align = 'end',
    sideOffset = 4,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
    return (
        <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
                data-slot="dropdown-menu-content"
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    'bg-popover text-popover-foreground z-50 max-h-[min(70vh,32rem)] min-w-56 overflow-y-auto overscroll-contain rounded-md border p-1 shadow-md ring-1 ring-black/5 outline-none',
                    className,
                )}
                {...props}
            />
        </DropdownMenuPrimitive.Portal>
    )
}

function DropdownMenuItem({
    className,
    variant = 'default',
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
    variant?: 'default' | 'destructive'
}) {
    return (
        <DropdownMenuPrimitive.Item
            data-slot="dropdown-menu-item"
            data-variant={variant}
            className={cn(
                // h-11 trên mobile để đạt vùng chạm 44px; thu về h-9 từ md trở lên.
                'relative flex h-11 cursor-pointer select-none items-center gap-2 rounded-sm px-2 text-sm outline-none md:h-9',
                'focus:bg-muted focus:text-foreground',
                'data-disabled:pointer-events-none data-disabled:opacity-50',
                'data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10',
                '[&_svg]:size-4 [&_svg]:shrink-0',
                className,
            )}
            {...props}
        />
    )
}

function DropdownMenuLabel({
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
    return (
        <DropdownMenuPrimitive.Label
            data-slot="dropdown-menu-label"
            className={cn(
                'px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground',
                className,
            )}
            {...props}
        />
    )
}

function DropdownMenuSeparator({
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
    return (
        <DropdownMenuPrimitive.Separator
            data-slot="dropdown-menu-separator"
            className={cn('-mx-1 my-1 h-px bg-border', className)}
            {...props}
        />
    )
}

export {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
}
